/** The loop fields a clip row carries where its `.msa` has a `LoopData` block
 *  (`RaceMotionData.cpp:372-386`). */
export interface LoopedClip {
  readonly durationS: number;
  readonly loopCount?: number;
  readonly loopStartS?: number;
  readonly loopEndS?: number;
}

/** How many times a motion goes back through its window: one pass fewer than
 *  a count above one, without end for -1, and never otherwise
 *  (`ActorInstanceMotion.cpp:175,181-182`). */
function wrapsOf(clip: LoopedClip): number {
  const { loopCount, loopStartS, loopEndS } = clip;
  if (loopCount === undefined || loopStartS === undefined || loopEndS === undefined) return 0;
  if (loopEndS <= loopStartS) return 0;
  if (loopCount === -1) return Infinity; // ActorInstanceMotion.cpp:175
  return Math.max(0, loopCount - 1); // ActorInstanceMotion.cpp:175,181-182
}

/** Where in its clip a one-shot is `elapsedS` after it started: each time it
 *  passes its window's end with a pass left, it goes back to the window's
 *  start (`ActorInstanceMotion.cpp:175-185`). */
export function oneShotLocalS(clip: LoopedClip, elapsedS: number): number {
  const wraps = wrapsOf(clip);
  if (wraps === 0 || elapsedS <= clip.loopEndS!) return elapsedS;
  const window = clip.loopEndS! - clip.loopStartS!;
  return elapsedS - Math.min(wraps, Math.ceil((elapsedS - clip.loopEndS!) / window)) * window;
}

/** How long a one-shot plays before it ends: its clip, and its window once
 *  more for each pass back through it. */
export function oneShotLengthS(clip: LoopedClip): number {
  const wraps = wrapsOf(clip);
  return wraps === 0 ? clip.durationS : clip.durationS + wraps * (clip.loopEndS! - clip.loopStartS!);
}
